import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";
import { ValueKind } from "../../../model";

const NBSP = " ";

/** Compute the issue list for a text value, or null if it's clean. */
function classifyWhitespace(value: string): string | null {
  if (value.length === 0) return null;
  const issues: string[] = [];
  if (value.trim() !== value) issues.push("leading/trailing whitespace");
  if (value.includes(NBSP)) issues.push("non-breaking space");
  return issues.length === 0 ? null : issues.join(", ");
}

export const whitespaceIssues: AuditRule = {
  id: "system.audit.whitespace-issues",
  name: "Whitespace issues",
  description:
    "Text cells with leading/trailing whitespace, or containing non-breaking spaces (often introduced by copy-paste from web pages or PDFs).",
  enabled: true,
  source: "system",
  severity: "info",
  /**
   * Per-text-id verdict cache: each unique text string is classified
   * once. At 1M cells where label values repeat heavily, this is a
   * small number of trim/includes calls instead of one per cell.
   *
   * Reads `cellValueKind === TEXT` to pre-filter — formulas returning
   * text and error literals are correctly excluded.
   */
  check(sheet, model) {
    const findings: AuditFindingPartial[] = [];
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellValueKind = sheet.cellValueKind;
    const textValueIds = sheet.textValueIds;
    const textValueStrings = model.catalogs.textValueStrings;

    // Per-text-id verdict cache. Map.has distinguishes "uncomputed"
    // (no entry) from "computed clean" (null) and "computed dirty"
    // (string).
    const issueByTextId = new Map<number, string | null>();

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const idx = rowBase + c;
        if (cellValueKind[idx] !== ValueKind.TEXT) continue;
        const id = textValueIds[idx];
        if (id < 0) continue;

        let issue = issueByTextId.get(id);
        if (issue === undefined && !issueByTextId.has(id)) {
          issue = classifyWhitespace(textValueStrings[id]);
          issueByTextId.set(id, issue);
        }
        if (issue === null || issue === undefined) continue;

        findings.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + r, startCol + c),
          message: issue,
        });
      }
    }
    return findings;
  },
};
